"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative bg-transparent px-0 py-1 text-[13px] font-default font-bold uppercase tracking-[0.1em] text-foreground transition-colors hover:bg-transparent hover:text-foreground focus:bg-transparent">
                <span>{item.label}</span>
                <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-4">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-4">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onNavigate} className="border-b border-border py-4 text-2xl font-default font-bold uppercase tracking-tight text-foreground transition-colors hover:text-primary">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1951" id="site-header" className={`sticky top-0 z-50 w-full border-b bg-background text-foreground transition-all duration-300 ${scrolled ? "border-border/50 bg-background/80 backdrop-blur-md" : "border-border bg-background"}`}>
      <div className={`mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 transition-all duration-300 sm:px-6 lg:px-8 ${scrolled ? "h-14" : "h-20"}`}>
        <div className="flex items-center">
          <Link to="/" aria-label="DTFPRINT home" className="flex items-center">
            <WvcLogo className="!w-[100px] !h-[100px] [&_svg]:!w-[100px] [&_svg]:!h-[100px]" />
          </Link>
        </div>

        <div className="hidden flex-1 items-center lg:flex lg:pl-10">
          <MenuProvider menu_id="22" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden items-center md:flex">
            {searchOpen ? <div className="flex items-center gap-1">
                <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search prints" className="[&_input]:h-9 [&_input]:w-56 [&_input]:rounded-md [&_input]:border-border [&_input]:bg-card [&_input]:text-foreground [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-primary" />
                <Button variant="ghost" size="icon" aria-label="Close search" onClick={() => {
              setSearchOpen(false);
              setSearchValue("");
            }} className="text-foreground hover:text-primary">
                  <X className="size-4" />
                </Button>
              </div> : <Button variant="ghost" size="icon" aria-label="Open search" onClick={() => setSearchOpen(true)} className="text-foreground hover:text-primary">
                <Search className="size-5" />
              </Button>}
          </div>

          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:text-primary [&_span]:bg-primary [&_span]:text-primary-foreground" />

          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:text-primary">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full border-l border-border bg-background sm:max-w-md">
                <SheetHeader className="px-4 pt-2">
                  <SheetTitle className="text-xs font-default font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Navigation
                  </SheetTitle>
                </SheetHeader>

                <div className="px-4">
                  <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search prints" className="[&_input]:h-11 [&_input]:w-full [&_input]:rounded-md [&_input]:border-border [&_input]:bg-card [&_input]:text-foreground [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-primary" />
                </div>

                <div className="mt-2 flex-1 overflow-y-auto">
                  <MenuProvider menu_id="22">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <SheetFooter className="px-4">
                  <SheetClose asChild>
                    <Link to="#" className="flex w-full items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-default font-bold uppercase tracking-[0.1em] text-primary-foreground transition-colors hover:bg-primary/90">
                      View Cart
                    </Link>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}